/**
 * Display texts of a billing feature (EB-4502).
 *
 * Three sources per feature code (English can be overridden, see ENGLISH_OVERRIDES):
 *   - English      — Localization bag `featureShowname/defTarif`, language 2,
 *                    falling back to the caller's static English label
 *   - KV text      — the wording staff know from the legacy KV
 *                    (`KV_FEATURE_LABELS`, copied from KundenBillingFrame.java),
 *                    corrected where the KV text is wrong
 *   - Invoice text — same bag, language 1: what the customer reads on the invoice
 *
 * Line 1 of a label is the English text, line 2 the KV text (invoice text when
 * KV does not offer the code). The help tooltip lists all of them.
 */
import type { LocalizationBag } from './bilingual-label.util';
import { KV_FEATURE_LABELS, KvFeatureEntry } from './kv-feature-labels';

/**
 * English texts that win over the Localization bag where the bag value is wrong.
 * `gut_credit`: bag (language 2) says "Credit Credits"; wording follows the KV text
 * "Gutschrift, Credits" (EB-4502, agreed with Torsten 2026-10-14).
 */
const ENGLISH_OVERRIDES: Readonly<Record<string, string>> = {
  gut_credit: 'Credit, credits',
};

/** First KV occurrence per code — used when the row's category is unknown. */
const FIRST_KV_ENTRY: ReadonlyMap<string, KvFeatureEntry> = (() => {
  const map = new Map<string, KvFeatureEntry>();
  for (const entry of Object.values(KV_FEATURE_LABELS).flat()) {
    if (!map.has(entry[0])) map.set(entry[0], entry);
  }
  return map;
})();

export interface FeatureTexts {
  code: string;
  english: string;
  /** KV text (corrected where needed), or `null` when KV does not offer the code. */
  kv: string | null;
  /** Original KV text — only set when `kv` is a correction of it. */
  kvOriginal: string | null;
  /** German invoice text, or `null` when the bag has no entry. */
  invoice: string | null;
}

/**
 * KV text of `code` in `categoryKey`. Without a (matching) category the first
 * KV occurrence of the code is used.
 */
export function kvFeatureText(
  code: string,
  categoryKey: string | null | undefined,
): { text: string; original: string | null } | null {
  const inCategory = categoryKey ? KV_FEATURE_LABELS[categoryKey]?.find(([c]) => c === code) : undefined;
  const entry = inCategory ?? FIRST_KV_ENTRY.get(code);
  if (!entry) return null;
  const [, kvText, corrected] = entry;
  return corrected ? { text: corrected, original: kvText } : { text: kvText, original: null };
}

/** Position of `code` in the KV dropdown of `categoryKey`; unknown codes sort last. */
export function kvOrderIndex(code: string, categoryKey: string | null | undefined): number {
  const index = categoryKey ? (KV_FEATURE_LABELS[categoryKey]?.findIndex(([c]) => c === code) ?? -1) : -1;
  return index === -1 ? Number.POSITIVE_INFINITY : index;
}

export function resolveFeatureTexts(
  code: string,
  categoryKey: string | null | undefined,
  bagEn: LocalizationBag,
  bagDe: LocalizationBag,
  fallbackEn: string,
): FeatureTexts {
  const kv = kvFeatureText(code, categoryKey);
  return {
    code,
    english: (ENGLISH_OVERRIDES[code] ?? (bagEn.get(code) || fallbackEn || code)).trim(),
    kv: kv?.text ?? null,
    kvOriginal: kv?.original ?? null,
    invoice: bagDe.get(code)?.trim() || null,
  };
}

/** Line 2 of the two-line label: KV text, else invoice text; `null` when it would repeat line 1. */
export function featureSecondLine(t: FeatureTexts): string | null {
  const second = t.kv ?? t.invoice;
  return second && second !== t.english ? second : null;
}

/** Single-line form `English (KV text)` — input value, delete confirm. */
export function featureOneLineLabel(t: FeatureTexts): string {
  const second = featureSecondLine(t);
  return second ? `${t.english} (${second})` : t.english;
}

/** Lower-cased haystack for type-ahead and grid search: every text plus the code. */
export function featureSearchText(t: FeatureTexts): string {
  return [t.english, t.kv, t.kvOriginal, t.invoice, t.code]
    .filter((s): s is string => !!s)
    .join(' ')
    .toLowerCase();
}
